import React from "react";
import Head from "next/head";
import Link from "next/link";
import Navigation from "@/layout/navigation";
import WhoWeServeFeatureSection from "@/components/who-we-serve/WhoWeServeFeatureSection";

export default function ForPersonalTrainersPageFr() {
  return (
    <>
      <Head>
        <title>Featcal pour Coachs sportifs | Régularité entre séances</title>
        <meta
          name="description"
          content="Featcal aide les coachs sportifs à garder les clients réguliers entre séances : suivi structuré, plans dans l’app et revue plus rapide-pour des résultats durables."
        />
      </Head>

      <header className="bg-gradient-to-b from-white from-20% to-blue-300 flex flex-col gap-16 items-center justify-between overflow-hidden px-8 xl:px-0 z-[51] relative pb-20">
        <Navigation />
        <div className="flex flex-col md:items-center md:justify-center gap-6 mt-2 z-10 md:text-center">
          <p className="text-sm font-semibold text-blue-700 bg-white/70 border border-blue-200 px-4 py-2 rounded-full w-fit md:mx-auto">
            Pour coachs sportifs
          </p>
          <h1 className="text-4xl md:text-5xl font-medium leading-tight max-w-3xl">
            Plus de régularité entre séances-sans plus d’admin.
          </h1>
          <p className="text-md max-w-2xl mx-auto text-slate-700">
            Une app client unique pour suivre et appliquer les plans, et un flow
            de revue clair pour vous-pour coacher la performance et l’adhérence
            sans courir après des captures d’écran.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-center">
            <Link
              href="/start"
              className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
            >
              Commencer gratuitement
            </Link>
            <Link
              href="/#faq"
              className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
            >
              Voir la FAQ
            </Link>
          </div>
        </div>
      </header>

      <main id="main-content" className="bg-white">
        <section className="py-16 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-3xl p-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
              Le vrai sujet : la régularité entre les séances
            </h2>
            <ul className="mt-4 space-y-3 text-slate-700">
              {[
                "Les workouts et habitudes ne tiennent pas sans structure au quotidien.",
                "Les updates arrivent en captures et DMs.",
                "Vous faites de l’admin au lieu de coacher la performance.",
              ].map((p) => (
                <li key={p} className="flex gap-3">
                  <span className="text-slate-400 font-bold">•</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
            {[
              {
                title: "Workouts livrés dans l’app",
                desc: "Un plan clair à suivre, avec moins d’allers-retours.",
              },
              {
                title: "Suivez ce qui compte",
                desc: "Workouts, pas, sommeil, hydratation, repas… accountability simple et cohérent.",
              },
              {
                title: "Revue progression plus rapide",
                desc: "Repérez vite les gaps d’adhérence et ajustez plus tôt.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30"
              >
                <h2 className="text-xl font-semibold text-slate-900 mb-3">
                  {card.title}
                </h2>
                <p className="text-slate-700 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0 pt-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div className="bg-white border border-slate-200 rounded-3xl p-8">
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                Idéal pour
              </h2>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Recomposition corporelle (training + nutrition).",
                  "Perte de masse grasse où l’adhérence drive les résultats.",
                  "Prise de masse / prise de poids avec suivi structuré.",
                  "Coaching hybride : check-ins + accountability entre séances.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                Ce que Featcal remplace
              </h3>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Excel et apps dispersées.",
                  "Renvois de plans et messages “c’est quoi mon plan ?”.",
                  "Captures d’écran pour les updates.",
                  "Relances pour l’adhérence entre les séances.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-slate-400 font-bold">•</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <WhoWeServeFeatureSection
          eyebrow="Plans d’entraînement"
          title="Des plans vraiment suivis"
          description="Livrez l’entraînement dans l’app : les clients savent quoi faire, et vous voyez l’adhérence sans courir après des captures."
          bullets={[
            "Moins d’admin et moins de renvois.",
            "Plus de régularité entre les séances.",
            "Revue de progression plus rapide pour ajuster plus tôt.",
          ]}
          imageSrc="/images/workout.webp"
          imageAlt="Aperçu du plan d’entraînement"
          align="right"
          imageFit="contain"
        />

        <section className="bg-gradient-to-b from-white to-blue-50 py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-blue-100 rounded-3xl p-8 md:p-10 shadow-xl shadow-blue-100/30">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
                  Prêt à améliorer l’adhérence entre les séances ?
                </h2>
                <p className="text-slate-700 mt-2">
                  Commencez gratuitement et gagnez du temps dès la prochaine
                  semaine.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  href="/start"
                  className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
                >
                  Commencer gratuitement
                </Link>
                <Link
                  href="/#faq"
                  className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
                >
                  Voir la FAQ
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
